import { BaseCrud } from '@/shared/models/cruds.models'
import { Component, Ref } from 'vue-property-decorator'
import ConfigurationSectionHeader from '@/modules/configurationModule/ConfigurationSectionHeader.vue'
import VtGrid from '@/components/VtGrid/VtGrid.vue'
import { GetContextMenuItemsParams, MenuItemDef } from '@ag-grid-enterprise/all-modules'
import i18n from '@/lang/i18n'
import BaseCrudServerSideComponent from '@/shared/resources/base-crud-server-side.component'
import { EntityIdentifiersRequest } from '@/shared/models/http/http.model'
import ModalWrapper from '@/components/ModalWrapper.vue'
import { ConfigurationAutomaticPreformattedByOntologyCreateRequest, ConfigurationAutomaticPreformattedByOntologyPatchInBatchRequest, ConfigurationAutomaticPreformattedByOntologyResponse } from '@/modules/configurationModule/detail/automation/preformatted/services/configuration-preformatted.dto'
import { AutomationPreformattedGridState, initialAutomationPreformattedGridState } from '@/modules/configurationModule/detail/automation/preformatted/components/preformatted-grid/configuration-automation-preformatted-grid.model'
import { preformattedConfigurationServices } from '@/modules/configurationModule/detail/automation/preformatted/services/configuration-preformatted.service'
import AutomationPreformattedModal from '@/modules/configurationModule/detail/automation/preformatted/components/preformatted-modal/configuration-automation-preformatted-modal.vue'
import AutomationPreformattedModalComponent from '@/modules/configurationModule/detail/automation/preformatted/components/preformatted-modal/configuration-automation-preformatted-modal'

@Component({
    components: {
        ConfigurationSectionHeader,
        VtGrid,
        AutomationPreformattedModal
    }
})
export default class AutomationPreformattedGrid extends BaseCrudServerSideComponent<ConfigurationAutomaticPreformattedByOntologyResponse> implements BaseCrud {
    @Ref() readonly localAutomationModalWrapper!: typeof ModalWrapper
    @Ref() readonly localAutomationModal!: AutomationPreformattedModalComponent

    state: AutomationPreformattedGridState
    constructor() {
        super()
        this.state = initialAutomationPreformattedGridState()
    }

    get canDelete(): boolean {
        return !!this.state.selectedRows.length
    }

    get canUpdate(): boolean {
        const numberRowSelected: number = this.state.selectedRows.length
        return numberRowSelected === 1
    }

    getContextMenuItems(params:GetContextMenuItemsParams): MenuItemDef[] {
        return [
            {
                name: this.$t('configuration.actions.update').toString(),
                cssClasses: this.state.selectedRows.length > 1 ? ['not-show'] : [''],
                action: () => this.onUpdate(params?.node?.data)
            },
            {
                name: this.$t('configuration.actions.delete').toString(),
                cssClasses: this.state.selectedRows.length < 1 ? ['not-show'] : [''],
                action: () => this.onDelete()
            }
        ]
    }

    async createOperation(row: ConfigurationAutomaticPreformattedByOntologyCreateRequest): Promise<void> {
        this.$_updateLoading(true)
        const [err, response] = await this.$_toCall(preformattedConfigurationServices.createPreformatted(this.state.labId, row), true)
        this.$_updateLoading(false)
        if (err) {
            return
        } else if (response?.length) {
            this.$_handleErrors(response)
            return
        }
        this.localAutomationModal.updateRow(response)
        this.state = {
            ...this.state,
            actionTitle: i18n.t('configuration.actions.update').toString()
        }
        this.$_showToastSuccess(this.$t('configuration.toastMessages.created')?.toString())
        this.reloadData()
    }

    async patchOperation(rows: ConfigurationAutomaticPreformattedByOntologyPatchInBatchRequest[]): Promise<void> {
        this.$_updateLoading(true)
        const [err, response] = await this.$_toCall(preformattedConfigurationServices.patchPreformatted(this.state.labId, rows), true)
        this.$_updateLoading(false)
        if (err) {
            return
        } else if (response?.length) {
            this.$_handleErrors(response)
            return
        }
        this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated', 1))
        this.reloadData()
    }

    async deleteOperation(rows: ConfigurationAutomaticPreformattedByOntologyResponse[]): Promise<void> {
        if (rows.length) {
            const selectedRowsSize:number = this.state?.selectedRows?.length
            const labelConfirmation:string = this.$t('configuration.grid.deleteConfirmation')?.toString()
            const labelGrid: string = this.$tc('configuration.grid.element', selectedRowsSize)
            const labelDeleteQuestion:string = this.$t('configuration.grid.deleteQuestion')?.toString()
            const labelDeleteAction:string = this.$t('configuration.actions.delete')?.toString()
            const promise = await this.$_showConfirmationModal(
                `${labelConfirmation} ${selectedRowsSize} ${labelGrid}, <br/> ${labelDeleteQuestion}`,
                labelDeleteAction
            )
            if (promise) {
                this._deleteBatchOperation(rows)
            }
        }
    }

    onAdd(): void {
        this.showModal(this.$t('configuration.actions.add').toString(), null, this.localAutomationModalWrapper)
    }

    onUpdate(defaultPortion: ConfigurationAutomaticPreformattedByOntologyResponse = this.state.gridApi?.getSelectedRows()[0]): void {
        if (defaultPortion) {
            this.showModal(this.$t('configuration.actions.update').toString(), defaultPortion, this.localAutomationModalWrapper)
        }
    }

    onDelete(): void {
        if (this.state.selectedRows.length) {
            this.deleteOperation(this.state.selectedRows)
        }
    }

    private async _deleteBatchOperation(rows: ConfigurationAutomaticPreformattedByOntologyResponse[]): Promise<void> {
        const rowsToDelete: EntityIdentifiersRequest = {
            entityIdentifiers: rows.map((template: ConfigurationAutomaticPreformattedByOntologyResponse) => Number(template?.id))
        }
        this.$_updateLoading(true)
        const [err, response] = await this.$_toCall(preformattedConfigurationServices.deletePreformatted(this.state.labId, rowsToDelete), true)
        this.$_updateLoading(false)
        if (err) {
            return
        } else if (response.length) {
            this.$_handleErrors(response)
            return
        }
        if (rows.length > 1) {
            this.$_showToastSuccess(
                this.$tc('configuration.toastMessages.deleted', 2, { elements: rows?.length })
            )
        } else {
            this.$_showToastSuccess(this.$tc('configuration.toastMessages.deleted', 1))
        }
        this.reloadData()
    }
}
